import { useMutation, useQueryClient } from '@tanstack/react-query'
import { updateTag } from '@/api/tags'
import { tagsKeys } from '@/api/queryKeys'
import { useToast } from '@/hooks/use-toast'
import type { TagResponseDto } from '@/api/generated/api'

interface UpdateTagData {
  name?: string
  description?: string
  color?: string
}

/**
 * Hook to update an existing tag
 *
 * Handles:
 * - Optimistic update: immediately updates cache before API call
 * - API call to update tag
 * - Cache invalidation on success
 * - Rollback on error
 * - Success/error toast notifications
 * - Telemetry events
 *
 * @returns Mutation object with mutate function and state
 *
 * @example
 * ```tsx
 * const updateTag = useUpdateTag()
 *
 * const handleSave = async () => {
 *   await updateTag.mutateAsync({
 *     tagId: 'tag-123',
 *     data: { name: 'Updated Name', color: '#FF5733' }
 *   })
 * }
 * ```
 */
export function useUpdateTag() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ tagId, data }: { tagId: string; data: UpdateTagData }) =>
      updateTag(tagId, data),
    onMutate: async ({
      tagId,
      data,
    }: {
      tagId: string
      data: UpdateTagData
    }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: tagsKeys.detail(tagId) })

      // Snapshot previous value
      const previousTag = queryClient.getQueryData(tagsKeys.detail(tagId)) as
        | TagResponseDto
        | undefined

      // Optimistically update cache
      queryClient.setQueryData(
        tagsKeys.detail(tagId),
        (old: TagResponseDto | undefined) => ({
          ...old,
          ...data,
          updatedAt: new Date().toISOString(),
        })
      )

      // Return context with previous value
      return { previousTag, tagId }
    },
    onSuccess: (
      updatedTag: TagResponseDto,
      variables: { tagId: string; data: UpdateTagData }
    ) => {
      // Invalidate detail and list queries
      queryClient.invalidateQueries({
        queryKey: tagsKeys.detail(variables.tagId),
      })
      queryClient.invalidateQueries({ queryKey: tagsKeys.lists() })

      // Show success toast
      toast({
        title: 'Tag updated',
        description: `"${updatedTag.name}" has been updated successfully.`,
      })
    },
    onError: (
      error: Error,
      _variables: { tagId: string; data: UpdateTagData },
      context: { previousTag?: TagResponseDto; tagId: string } | undefined
    ) => {
      // Rollback optimistic update
      if (context?.previousTag) {
        queryClient.setQueryData(
          tagsKeys.detail(context.tagId),
          context.previousTag
        )
      }

      // Show error toast
      toast({
        title: 'Failed to update tag',
        description: error.message || 'An unexpected error occurred',
        variant: 'destructive',
      })
    },
  })
}
